/**
 * Pool System Control (/pool/system) — arms/disarms the license-removal job itself. `_manage`-gated
 * (pool role owner OR admin — rev 6 R2: the principal explicitly extended this to pool-Admin after an
 * "อิงแนะ Owner-only" risk flag, so keep it that way rather than re-litigating), gated client-side on
 * `usePoolAccess()` BEFORE any system-state request fires: a caller with no pool role at all (or only
 * `"viewer"`) gets the app's normal RBAC-forbidden copy here, NOT <PoolErrorState/> — that copy is
 * reserved for "not a pool operator at all", a different story from "you're a pool operator but not
 * manage-capable".
 *
 * rev 17c (23/7, คุณใหม่: "ถ้าจะเทส คือเทสจริงด้วย user test") — Dry-run mode is GONE from the UI
 * entirely. Two states only, driven by {enabled, dry_run}: OFF (false, true) never touches a license ·
 * LIVE (true, false) removes real customer licenses. `PoolSystemStateName` (api/types.ts) still carries
 * "dry_run" as a THIRD wire value because the server can still legitimately report it (a state set before
 * this rev, or set directly against the API) — never re-litigated away from this screen, this UI simply
 * no longer offers switching TO it. `toDisplayState()` below collapses that legacy value down to the
 * "off" label/tone/explanation (it never touched Zoom either way, same as a real "off"), with a small
 * note surfaced separately so an operator isn't left wondering why the badge doesn't match what they
 * remember setting. Arming LIVE requires typing the word "LIVE" (exact, case-sensitive) before the PUT
 * fires with confirm:"LIVE" — the server 422s without it, rendered verbatim, same as every other refusal
 * in this module. A hard_disabled system (the .env break-glass) 409s any write, also rendered verbatim;
 * the controls are disabled up front so an operator isn't invited to try one that can only 409.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import {
  Button,
  ErrorState,
  Input,
  Modal,
  SkeletonRows,
  StatusPill,
  useNotifier,
} from "@/components/ui";
import { usePoolAccess, usePoolSystemState, useSetPoolSystemState } from "@/features/pool/hooks";
import { formatInstant } from "@/lib/format";
import type { PoolSetSystemStateBody, PoolSystemStateName } from "@/api/types";
import type { TKey } from "@/i18n/keys";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** The two states this screen offers switching TO (rev 17c — Dry-run dropped). Distinct from the wire
 *  type `PoolSystemStateName`, which keeps its legacy third value — see this file's own rev 17c banner. */
type PoolSystemDisplayState = "off" | "live";

/** A legacy `dry_run` report from the server reads as "off" everywhere in this UI (see this file's own
 *  rev 17c banner) — it never called Zoom for real either, same as a genuine off. */
function toDisplayState(state: PoolSystemStateName): PoolSystemDisplayState {
  return state === "live" ? "live" : "off";
}

const STATE_ORDER = ["off", "live"] as const satisfies readonly PoolSystemDisplayState[];

const STATE_LABEL_KEY: Record<PoolSystemDisplayState, TKey> = {
  off: "pool.system.stateOff",
  live: "pool.system.stateLive",
};

const STATE_EXPLAIN_KEY: Record<PoolSystemDisplayState, TKey> = {
  off: "pool.system.explainOff",
  live: "pool.system.explainLive",
};

const STATE_SWITCH_LABEL_KEY: Record<PoolSystemDisplayState, TKey> = {
  off: "pool.system.switchToOff",
  live: "pool.system.switchToLive",
};

const STATE_TONE: Record<PoolSystemDisplayState, "neutral" | "danger"> = {
  off: "neutral",
  live: "danger",
};

/** {enabled, dry_run} for a PUT targeting each state — "live" additionally needs confirm:"LIVE", added
 *  by the caller once the operator has typed it, never baked in here. `dry_run` stays `true` on the OFF
 *  body only because it's what the wire contract's `enabled:false` write has always sent (server ignores
 *  it once `enabled` is false) — not a UI-selectable dry-run mode, see this file's own rev 17c banner. */
const TARGET_BODY: Record<
  PoolSystemDisplayState,
  Pick<PoolSetSystemStateBody, "enabled" | "dry_run">
> = {
  off: { enabled: false, dry_run: true },
  live: { enabled: true, dry_run: false },
};

export function PoolSystemPage() {
  const { t } = useTranslation();
  const accessQ = usePoolAccess();

  if (accessQ.isPending) return <SkeletonRows rows={3} />;
  if (accessQ.isError) {
    return <ErrorState error={accessQ.error} onRetry={() => void accessQ.refetch()} />;
  }

  const role = accessQ.data.role;
  if (role !== "owner" && role !== "admin") {
    return <ErrorState title={t("state.forbiddenTitle")} body={t("state.forbiddenBody")} />;
  }

  return <PoolSystemControl />;
}

function PoolSystemControl() {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const stateQ = usePoolSystemState();
  const setMut = useSetPoolSystemState();

  const [confirmTarget, setConfirmTarget] = useState<PoolSystemDisplayState | null>(null);
  const [liveText, setLiveText] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  function closeDialog() {
    setConfirmTarget(null);
    setLiveText("");
    setSubmitError(null);
  }

  function submit(target: PoolSystemDisplayState) {
    setSubmitError(null);
    const body: PoolSetSystemStateBody =
      target === "live" ? { ...TARGET_BODY.live, confirm: liveText.trim() } : TARGET_BODY[target];
    setMut.mutate(body, {
      onSuccess: (res) => {
        notify({
          tone: "ok",
          title: t("pool.system.switchSuccess", {
            state: t(STATE_LABEL_KEY[toDisplayState(res.state)]),
          }),
        });
        closeDialog();
      },
      // Verbatim server refusal — the 422 (missing/wrong confirm) or 409 (hard_disabled) message,
      // exactly like every other guardrail refusal in this module. Dialog stays open so it's readable.
      onError: (e) => setSubmitError(errMessage(e, t("state.errorTitle"))),
    });
  }

  if (stateQ.isPending) return <SkeletonRows rows={3} />;
  if (stateQ.isError) {
    return <ErrorState error={stateQ.error} onRetry={() => void stateQ.refetch()} />;
  }

  const data = stateQ.data;
  const locked = data.hard_disabled;
  const displayState = toDisplayState(data.state);
  const isLegacyDryRun = data.state === "dry_run";

  return (
    <section aria-labelledby="pool-system-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1
          id="pool-system-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {t("pool.system.title")}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("pool.system.subtitle")}</p>
      </header>

      <div className="flex flex-col gap-4 rounded-(--radius) border border-(--border) bg-(--surface) p-5 shadow-(--shadow-sm)">
        <div className="flex flex-wrap items-center gap-3">
          {/* LIVE is deliberately louder than the StatusPill default — this is the one state where the
              system removes real customer licenses, so it gets extra weight, not just a colour swap. */}
          <StatusPill
            tone={STATE_TONE[displayState]}
            {...(displayState === "live"
              ? {
                  className:
                    "px-3 py-1 text-(length:--text-sm) font-semibold ring-2 ring-(--danger) ring-offset-1",
                }
              : {})}
          >
            {t(STATE_LABEL_KEY[displayState])}
          </StatusPill>
          <p className="text-(length:--text-sm) text-(--text)">
            {t(STATE_EXPLAIN_KEY[displayState])}
          </p>
        </div>

        {/* rev 17c — a legacy dry_run report from the server reads as Off above; this note is the only
            place that distinction still surfaces, so an operator who remembers setting dry-run isn't
            left confused by a badge that now just says "Off". */}
        {isLegacyDryRun && (
          <p className="text-(length:--text-xs) text-(--text-subtle)">
            {t("pool.system.legacyDryRunNote")}
          </p>
        )}

        <p className="text-(length:--text-xs) text-(--text-muted)">
          {data.updated_by && data.updated_at
            ? t("pool.system.updatedBy", {
                who: data.updated_by,
                when: formatInstant(data.updated_at),
              })
            : t("pool.system.neverUpdated")}
        </p>

        {data.source === "settings" && (
          <p className="text-(length:--text-xs) text-(--text-subtle)">
            {t("pool.system.settingsSourceNote")}
          </p>
        )}

        {locked && (
          <p role="alert" className="text-(length:--text-xs) font-medium text-(--danger)">
            {t("pool.system.hardDisabledNote")}
          </p>
        )}

        <div className="flex flex-wrap gap-2 border-t border-(--border-hairline) pt-4">
          {STATE_ORDER.map((target) => (
            <Button
              key={target}
              size="sm"
              variant={target === "live" ? "danger" : "secondary"}
              disabled={locked || data.state === target}
              onClick={() => setConfirmTarget(target)}
            >
              {t(STATE_SWITCH_LABEL_KEY[target])}
            </Button>
          ))}
        </div>
      </div>

      {/* OFF: a normal confirm dialog — no typing required. (rev 17c: Dry-run dropped, so this branch
          is OFF-only now — no more ternary between two non-LIVE targets.) */}
      {confirmTarget === "off" && (
        <Modal
          open
          onClose={closeDialog}
          title={t("pool.system.confirmOffTitle")}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={closeDialog}>
                {t("action.cancel")}
              </Button>
              <Button variant="danger" loading={setMut.isPending} onClick={() => submit("off")}>
                {t(STATE_SWITCH_LABEL_KEY.off)}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p className="text-(length:--text-sm) text-(--text)">
              {t("pool.system.confirmOffBody")}
            </p>
            {submitError && (
              <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                {submitError}
              </p>
            )}
          </div>
        </Modal>
      )}

      {/* LIVE: the operator must TYPE the word LIVE (exact, case-sensitive) — the confirm button stays
          disabled until it matches, so the PUT can only ever be sent with confirm:"LIVE" from this UI. */}
      {confirmTarget === "live" && (
        <Modal
          open
          onClose={closeDialog}
          title={t("pool.system.liveDialogTitle")}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={closeDialog}>
                {t("action.cancel")}
              </Button>
              <Button
                variant="danger"
                loading={setMut.isPending}
                disabled={liveText !== "LIVE"}
                onClick={() => submit("live")}
              >
                {t("pool.system.liveConfirmButton")}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <p role="alert" className="text-(length:--text-sm) font-medium text-(--danger)">
              {t("pool.system.liveDialogWarning")}
            </p>
            <label className="flex flex-col gap-1.5">
              <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                {t("pool.system.liveTypeToConfirmLabel")}
              </span>
              <Input
                value={liveText}
                onChange={(e) => setLiveText(e.target.value)}
                placeholder={t("pool.system.liveTypeToConfirmPlaceholder")}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            {submitError && (
              <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                {submitError}
              </p>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
